import React, { useEffect, useMemo, useState } from "react";

import { collection, getDocs, getDoc, doc, query, where } from "firebase/firestore";
import { auth, db } from "../../firebase";
import { useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";

import "./PortalDashboard.css";

const ASSETS = {
  webCatalog:
    "https://firebasestorage.googleapis.com/v0/b/gofoton-55796.firebasestorage.app/o/stockImages%2FGofoton%20Web%20Catalog%20logo.png?alt=media&token=3f4bee28-d8e5-42be-a85a-b1fdd7d9d356",

  truck:
    "https://firebasestorage.googleapis.com/v0/b/gofoton-55796.firebasestorage.app/o/stockImages%2FgofotonTruck%201.png?alt=media&token=398a8802-a581-49ed-bbb6-5301e7d08c96",

  masterParts:
    "https://firebasestorage.googleapis.com/v0/b/gofoton-55796.firebasestorage.app/o/stockImages%2Fmaster%20parts%20list%20icon.png?alt=media&token=c009f10f-fd74-4780-9dc3-eeabfa6516fa",

  resources:
    "https://firebasestorage.googleapis.com/v0/b/gofoton-55796.firebasestorage.app/o/stockImages%2FResources%20icon.png?alt=media&token=0c5582a3-c75e-40ed-9b7a-e87064025a95",

  fallbackClientLogo:
    "https://firebasestorage.googleapis.com/v0/b/gofoton-55796.firebasestorage.app/o/stockImages%2FV%20Verizon%20logo.png?alt=media&token=ca3e7d18-a3cf-4dbe-969e-f928e5d15cca",
};

const NAV_TABS = [
  {
    key: "dashboard",
    label: "Dashboard",
  },
  {
    key: "catalog",
    label: "Catalog",
  },
  {
    key: "orderStatus",
    label: "Order Status",
  },
  {
    key: "documents",
    label: "Documents",
  },
  {
    key: "contact",
    label: "Contact Us",
  },
  {
    key: "settings",
    label: "Settings",
  },
];

export default function PortalDashboard({ portalClient }) {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("dashboard");

  const [client, setClient] = useState({});
  const [clientLoading, setClientLoading] = useState(true);

  useEffect(() => {
    const loadPortalClient = async () => {
      try {
        const clientId = portalClient?.id;

        if (!clientId) {
          console.error("No authenticated portal client ID found.");

          setClientLoading(false);
          return;
        }

        const clientRef = doc(db, "portalClients", clientId);

        const clientSnap = await getDoc(clientRef);

        if (clientSnap.exists()) {
          const firestoreData = clientSnap.data();

          setClient({
            ...portalClient,
            ...firestoreData,
            id: clientSnap.id,
          });
        } else {
          console.error(`portalClients/${clientId} does not exist in Firestore`);

          setClient(portalClient);
        }
      } catch (err) {
        console.error("Error loading portal client:", err);
      } finally {
        setClientLoading(false);
      }
    };

    loadPortalClient();
  }, [portalClient]);

  const activeLabel = NAV_TABS.find((tab) => tab.key === activeTab)?.label || "Dashboard";

  const handleLogout = async () => {
    try {
      await signOut(auth);

      sessionStorage.removeItem("portalClient");

      navigate("/login?mode=client", {
        replace: true,
      });
    } catch (err) {
      console.error("Portal logout error:", err);
    }
  };

  const renderTab = () => {
    switch (activeTab) {
      case "dashboard":
        return <DashboardView client={client} setActiveTab={setActiveTab} />;

      case "catalog":
        return <CatalogView client={client} />;

      case "orderStatus":
        return <OrderStatusView client={client} />;

      case "documents":
        return <DocumentsView client={client} />;

      case "contact":
        return <ContactView client={client} />;

      case "settings":
        return <SettingsView client={client} onLogout={handleLogout} />;

      default:
        return null;
    }
  };

  if (clientLoading) {
    return (
      <div
        style={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        Loading portal...
      </div>
    );
  }

  return (
    <div className="portal-shell">
      {/* SIDEBAR */}

      <aside className="portal-sidebar">
        <div className="portal-clientBrand">
          <img
            src={client.logoURL || ASSETS.fallbackClientLogo}
            alt={client.name || "Client"}
            className="portal-clientLogo"
          />

          <div>
            <div className="portal-clientName">{client.name || "Client"}</div>

            <div className="portal-clientSubtitle">Dashboard</div>
          </div>
        </div>

        <nav className="portal-nav">
          {NAV_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`portal-navItem ${activeTab === tab.key ? "is-active" : ""}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </aside>

      {/* MAIN CONTENT */}

      <main className="portal-main">
        <header className="portal-topbar">
          <h1>{activeLabel}</h1>
        </header>

        <section className="portal-content">{renderTab()}</section>
      </main>
    </div>
  );
}

/* =====================================================
   DASHBOARD
   ===================================================== */

function DashboardView({ client, setActiveTab }) {
  return (
    <div className="portal-dashboardGrid">
      <DashboardCard
        image={ASSETS.webCatalog}
        title="Web Catalog"
        description="Research products, find drawings and datasheets, and access the punch out tool (requires a personal Verizon-approved login)."
        onClick={() => setActiveTab("catalog")}
      />

      <DashboardCard
        image={ASSETS.truck}
        title="Order Status"
        description="Search Verizon orders by PO or tracking number, view shipment details, and access proof of delivery information."
        onClick={() => setActiveTab("orderStatus")}
      />

      <DashboardCard
        image={ASSETS.masterParts}
        title="Master Part List"
        description="For Verizon and Verizon partners to find part numbers and ordering information."
        onClick={() => setActiveTab("documents")}
      />

      <DashboardCard
        image={ASSETS.resources}
        title="Knowledge Center"
        description="Find brochures, datasheets, white papers, case studies, videos, blogs, and more."
        onClick={() => window.open("/knowledge-center", "_blank", "noopener,noreferrer")}
      />

      {/* BRIAN */}

      <div className="portal-contactCard">
        <img
          src="https://firebasestorage.googleapis.com/v0/b/gofoton-55796.firebasestorage.app/o/stockImages%2F1779934321586.png?alt=media&token=af313ef8-93a6-4847-9b91-0b9bcc644fb3"
          alt="Brian"
          className="portal-contactPhoto"
        />

        <h2>Account Manager</h2>

        <p className="portal-contactName">{client.contactName || "Brian"}</p>

        {client.contactEmail && (
          <a href={`mailto:${client.contactEmail}`} className="portal-contactEmail portal-contactEmailSmall">
            {client.contactEmail}
          </a>
        )}

        {client.contactEmail && (
          <div className="portal-contactButtons">
            <a className="portal-orangeButton" href={`mailto:${client.contactEmail}`}>
              Contact Brian
            </a>
          </div>
        )}
      </div>

      {/* MAHENDRA */}

      <div className="portal-contactCard">
        <img
          src="https://firebasestorage.googleapis.com/v0/b/gofoton-55796.firebasestorage.app/o/stockImages%2Fmahendra.jpeg?alt=media&token=7824d585-9d63-44c2-ab51-28931aecd1a1"
          alt="Mahendra Persad"
          className="portal-contactPhoto"
        />

        <h2>Technical Support</h2>

        <p className="portal-contactName">Mahendra Persad</p>

        <a href="mailto:mahendra.persad@gofoton.com" className="portal-contactEmail portal-contactEmailSmall">
          mahendra.persad@gofoton.com
        </a>

        <div className="portal-contactButtons">
          <a className="portal-orangeButton" href="mailto:mahendra.persad@gofoton.com">
            Contact Mahendra
          </a>

          <a className="portal-orangeButton" href={`mailto:${client.supportEmail || "support@gofoton.com"}`}>
            Technical Support
          </a>
        </div>
      </div>
    </div>
  );
}

function DashboardCard({ image, title, description, onClick }) {
  return (
    <div className="portal-dashboardCard">
      <div className="portal-dashboardImage">
        <img src={image} alt={title} />
      </div>

      <h2>{title}</h2>

      {description && <p className="portal-dashboardDescription">{description}</p>}

      <button type="button" className="portal-orangeButton" onClick={onClick}>
        View
      </button>
    </div>
  );
}

/* =====================================================
   CATALOG
   ===================================================== */

function CatalogView({ client }) {
  return (
    <div className="portal-section">
      <div className="portal-largeCard">
        <img src={ASSETS.webCatalog} alt="Go!Foton Web Catalog" className="portal-catalogLogo" />

        <h2>Web Catalog</h2>

        <p>Access your Go!Foton product catalog and view all part numbers.</p>

        {client.catalogURL ? (
          <a className="portal-orangeButton" href={client.catalogURL} target="_blank" rel="noopener noreferrer">
            Open Catalog
          </a>
        ) : (
          <p className="portal-muted">Catalog access has not been configured yet.</p>
        )}

        <div className="portal-catalogAccessNote">
          <p>
            Don't have catalog access?{" "}
            <a href="https://portal.gofoton.com/catalog/access.html" target="_blank" rel="noopener noreferrer">
              Request access here.
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
/* =====================================================
   ORDER STATUS
   ===================================================== */

function getOrderTrackingNumber(order) {
  return String(
    order?.trackingNumber ||
      order?.["Tracking Number"] ||
      order?.tracking_number ||
      order?.trackingNo ||
      order?.tracking ||
      order?.trackingCode ||
      ""
  ).trim();
}

function cleanDateValue(value) {
  if (!value) return "—";

  const cleaned = String(value).trim().toUpperCase();

  if (["SSDNA", "N/A", "NA", "NONE", "NULL", "TBD", "-"].includes(cleaned)) {
    return "—";
  }

  return formatDateValue(value);
}

async function trackingMoreRequest(url, options = {}) {
  let action = "";

  if (url.includes("/couriers/detect")) {
    action = "detect";
  } else if (url.includes("/trackings/create")) {
    action = "create";
  } else if (url.includes("/trackings/get")) {
    action = "get";
  } else {
    throw new Error("Unsupported tracking request.");
  }

  let payload = {
    action,
  };

  if (options.body) {
    try {
      payload = {
        ...payload,
        ...JSON.parse(options.body),
      };
    } catch {
      throw new Error("Invalid tracking request.");
    }
  }

  if (action === "get") {
    const parsedUrl = new URL(url);

    payload.tracking_number = parsedUrl.searchParams.get("tracking_numbers") || "";

    payload.courier_code = parsedUrl.searchParams.get("courier_code") || "";
  }

  const response = await fetch("/portal-api/tracking", {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  const text = await response.text();

  let result = {};

  try {
    result = text ? JSON.parse(text) : {};
  } catch {
    result = {
      raw: text,
    };
  }

  if (!response.ok) {
    throw new Error(result?.error || result?.message || result?.raw || `Tracking request failed (${response.status})`);
  }

  return result;
}

async function detectTrackingMoreCourier(trackingNumber) {
  const result = await trackingMoreRequest("https://api.trackingmore.com/v4/couriers/detect", {
    method: "POST",

    body: JSON.stringify({
      tracking_number: trackingNumber,
    }),
  });

  const candidates = Array.isArray(result?.data)
    ? result.data
    : Array.isArray(result?.data?.items)
      ? result.data.items
      : [];

  console.log("TrackingMore carrier candidates:", candidates);

  const courier = candidates.find((item) => item?.courier_code) || candidates[0];

  const courierCode = courier?.courier_code || courier?.code || "";

  if (!courierCode) {
    throw new Error(`Could not detect carrier for ${trackingNumber}`);
  }

  return {
    courierCode,

    courierName: courier?.courier_name || courier?.name || courierCode,
  };
}

async function fetchLiveTracking(trackingNumber) {
  console.log("Looking up tracking number:", trackingNumber);

  const detected = await detectTrackingMoreCourier(trackingNumber);

  console.log("Detected carrier:", detected);

  try {
    const createResult = await trackingMoreRequest("https://api.trackingmore.com/v4/trackings/create", {
      method: "POST",

      body: JSON.stringify({
        tracking_number: trackingNumber,
        courier_code: detected.courierCode,
      }),
    });

    console.log("TrackingMore CREATE result:", createResult);

    const rawData = extractTrackingMoreRecord(createResult, trackingNumber);

    if (rawData) {
      const normalized = normalizeTrackingMore(rawData);

      normalized.carrier = normalizeCarrierName(normalized.carrier || detected.courierName || detected.courierCode);

      return normalized;
    }
  } catch (error) {
    console.warn("Create tracking failed, trying existing tracking:", error.message);
  }

  /*
    If this number was already created
    previously, retrieve the existing record.
  */
  const url =
    "https://api.trackingmore.com/v4/trackings/get" +
    `?tracking_numbers=${encodeURIComponent(trackingNumber)}` +
    `&courier_code=${encodeURIComponent(detected.courierCode)}`;

  const getResult = await trackingMoreRequest(url, {
    method: "GET",
  });

  console.log("TrackingMore GET result:", getResult);

  const rawData = extractTrackingMoreRecord(getResult, trackingNumber);

  if (!rawData) {
    throw new Error(`No live tracking information returned for ${trackingNumber}`);
  }

  const normalized = normalizeTrackingMore(rawData);

  normalized.carrier = normalizeCarrierName(normalized.carrier || detected.courierName || detected.courierCode);

  return normalized;
}

function extractTrackingMoreRecord(result, trackingNumber) {
  if (!result) return null;

  const data = result?.data;

  if (Array.isArray(data)) {
    return (
      data.find((item) => String(item?.tracking_number || "").trim() === String(trackingNumber).trim()) ||
      data[0] ||
      null
    );
  }

  if (Array.isArray(data?.items)) {
    return (
      data.items.find((item) => String(item?.tracking_number || "").trim() === String(trackingNumber).trim()) ||
      data.items[0] ||
      null
    );
  }

  if (data && typeof data === "object") {
    return data;
  }

  return null;
}

function normalizeTrackingMore(data = {}) {
  const originInfo = data?.origin_info || {};

  const destinationInfo = data?.destination_info || {};

  const checkpoints = Array.isArray(originInfo?.trackinfo)
    ? originInfo.trackinfo
    : Array.isArray(data?.trackinfo)
      ? data.trackinfo
      : Array.isArray(data?.checkpoints)
        ? data.checkpoints
        : [];

  const events = checkpoints
    .map((event) => {
      const location =
        event?.location || [event?.city, event?.state, event?.postal_code, event?.country].filter(Boolean).join(", ");

      return {
        dateTime: event?.checkpoint_date || event?.Date || event?.datetime || event?.time || "",

        status:
          event?.checkpoint_delivery_status ||
          event?.checkpoint_delivery_substatus ||
          event?.StatusDescription ||
          event?.status ||
          "",

        description: event?.tracking_detail || event?.Details || event?.message || event?.description || "",

        location,
      };
    })
    .filter((event) => event.dateTime || event.status || event.description || event.location)
    .sort((a, b) => {
      const aTime = new Date(a.dateTime).getTime();

      const bTime = new Date(b.dateTime).getTime();

      if (Number.isNaN(aTime) || Number.isNaN(bTime)) {
        return 0;
      }

      return bTime - aTime;
    });

  const newest = events[0] || null;

  const deliveryStatus = data?.delivery_status || data?.status || data?.track_status || newest?.status || "";

  const latestEvent = data?.latest_event || "";

  return {
    trackingNumber: data?.tracking_number || "",

    carrier: data?.courier_code || originInfo?.courier_code || data?.carrier || "",

    status: deliveryStatus,

    estimatedDelivery: data?.scheduled_delivery_date || data?.estimated_delivery_date || "",

    shipDate: data?.shipping_date || data?.ship_date || data?.created_at || "",

    deliveredAt: getDeliveredDate(deliveryStatus, events),

    currentLocation: newest?.location || "",

    lastUpdate: newest?.dateTime || data?.updated_at || "",

    lastMessage: newest?.description || latestEvent || "",

    serviceLevel: data?.service_code || data?.service || "",

    origin: originInfo?.courier_address || "",

    destination: destinationInfo?.courier_address || data?.scheduled_address || "",

    signedBy: extractSignedBy(events),

    proofOfDeliveryUrl: data?.proof_of_delivery_url || "",

    events,
  };
}

function normalizeCarrierName(carrier) {
  const value = String(carrier || "")
    .trim()
    .toLowerCase();

  if (!value) {
    return "";
  }

  if (value === "ups" || value.includes("united parcel service")) {
    return "UPS";
  }

  if (value === "usps" || value.includes("united states postal")) {
    return "USPS";
  }

  if (value.includes("fedex")) {
    return "FedEx";
  }

  if (value.includes("dhl")) {
    return "DHL";
  }

  return value
    .split(/[\s_-]+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function getDeliveredDate(status, events) {
  const isDelivered = String(status || "")
    .toLowerCase()
    .includes("delivered");

  if (!isDelivered) {
    return "";
  }

  const deliveredEvent = events.find((event) => {
    const combined = `${event.status || ""} ${event.description || ""}`.toLowerCase();

    return combined.includes("delivered");
  });

  return deliveredEvent?.dateTime || "";
}

function extractSignedBy(events) {
  for (const event of events) {
    const text = String(event.description || "");

    const signedMatch = text.match(/signed\s+for\s+by[:\s]+(.+)/i);

    if (signedMatch?.[1]) {
      return signedMatch[1].trim();
    }

    const receivedMatch = text.match(/received\s+by[:\s]+(.+)/i);

    if (receivedMatch?.[1]) {
      return receivedMatch[1].trim();
    }
  }

  return "";
}

function OrderStatusView({ client }) {
  const orderTypes = useMemo(
    () => [
      {
        key: "po",
        label: "PO Number",
        inputLabel: "PO Number",
        field: "poNumber",
      },

      {
        key: "tracking",
        label: "Tracking Number",
        inputLabel: "Tracking Number",
        field: "trackingNumber",
      },

      {
        key: "proof",
        label: "Proof of Delivery",
        inputLabel: "Tracking Number",
        field: "trackingNumber",
      },
    ],
    []
  );

  const [orderType, setOrderType] = useState("po");

  const [queryValue, setQueryValue] = useState("");

  const [orders, setOrders] = useState([]);

  const [results, setResults] = useState([]);

  const [loadingOrders, setLoadingOrders] = useState(true);

  const [searched, setSearched] = useState(false);

  const [trackingByNumber, setTrackingByNumber] = useState({});

  const [loadingTrackingNumbers, setLoadingTrackingNumbers] = useState({});

  const [trackingErrors, setTrackingErrors] = useState({});

  const selectedType = orderTypes.find((item) => item.key === orderType) || orderTypes[0];

  useEffect(() => {
    const loadOrders = async () => {
      if (!client?.id) {
        setLoadingOrders(false);

        return;
      }

      setLoadingOrders(true);

      try {
        const ordersQuery = query(
          collection(db, "portalOrders"),

          where("clientId", "==", client.id)
        );

        const snapshot = await getDocs(ordersQuery);

        const rows = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,

          ...docSnap.data(),
        }));

        setOrders(rows);

        console.log("Loaded portal orders:", rows.length);
      } catch (err) {
        console.error("Error loading portal orders:", err);
      } finally {
        setLoadingOrders(false);
      }
    };

    loadOrders();
  }, [client?.id]);

  const normalizeSearchValue = (value) =>
    String(value || "")
      .trim()
      .toLowerCase();

  const findMatchingOrders = () => {
    const search = normalizeSearchValue(queryValue);

    if (!search) {
      return [];
    }

    if (orderType === "tracking" || orderType === "proof") {
      return orders.filter((order) => normalizeSearchValue(getOrderTrackingNumber(order)) === search);
    }

    return orders.filter((order) => normalizeSearchValue(order[selectedType.field]) === search);
  };

  const loadTrackingForNumbers = async (trackingNumbers) => {
    const uniqueNumbers = [...new Set(trackingNumbers.filter(Boolean))];

    if (uniqueNumbers.length === 0) {
      console.log("No tracking numbers exist on these order lines yet.");

      return;
    }

    await Promise.all(
      uniqueNumbers.map(async (trackingNumber) => {
        setLoadingTrackingNumbers((prev) => ({
          ...prev,

          [trackingNumber]: true,
        }));

        setTrackingErrors((prev) => ({
          ...prev,

          [trackingNumber]: "",
        }));

        try {
          const liveTracking = await fetchLiveTracking(trackingNumber);

          setTrackingByNumber((prev) => ({
            ...prev,

            [trackingNumber]: liveTracking,
          }));
        } catch (error) {
          console.error("Tracking lookup failed:", trackingNumber, error);

          setTrackingErrors((prev) => ({
            ...prev,

            [trackingNumber]: error?.message || "Live tracking lookup failed.",
          }));
        } finally {
          setLoadingTrackingNumbers((prev) => ({
            ...prev,

            [trackingNumber]: false,
          }));
        }
      })
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const search = normalizeSearchValue(queryValue);

    if (!search) return;

    let matchingOrders = findMatchingOrders();

    if (orderType === "proof") {
      matchingOrders = matchingOrders.filter((order) => Boolean(getOrderTrackingNumber(order)));
    }

    console.log("MATCHING ORDERS:", matchingOrders);

    setResults(matchingOrders);

    setSearched(true);

    const trackingNumbers = matchingOrders.map(getOrderTrackingNumber).filter(Boolean);

    console.log("TRACKING NUMBERS BEING LOOKED UP:", trackingNumbers);

    await loadTrackingForNumbers(trackingNumbers);
  };

  const getCarrierTrackingURL = (order, liveTracking) => {
    const tracking = getOrderTrackingNumber(order);

    if (!tracking) {
      return null;
    }

    const carrier = String(liveTracking?.carrier || order?.carrier || "").toUpperCase();

    if (carrier.includes("UPS") || tracking.toUpperCase().startsWith("1Z")) {
      return `https://www.ups.com/track?loc=en_US&tracknum=${encodeURIComponent(tracking)}`;
    }

    if (carrier.includes("FEDEX")) {
      return `https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(tracking)}`;
    }

    if (carrier.includes("USPS")) {
      return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${encodeURIComponent(tracking)}`;
    }

    if (carrier.includes("DHL")) {
      return `https://www.dhl.com/us-en/home/tracking/tracking-express.html?submit=1&tracking-id=${encodeURIComponent(
        tracking
      )}`;
    }

    return null;
  };

  return (
    <div className="portal-orderStatus">
      <div className="portal-orderTabs">
        {orderTypes.map((item) => (
          <button
            type="button"
            key={item.key}
            className={`portal-orderTab ${orderType === item.key ? "is-active" : ""}`}
            onClick={() => {
              setOrderType(item.key);

              setQueryValue("");

              setResults([]);

              setSearched(false);

              setTrackingErrors({});
            }}
          >
            {item.label}
          </button>
        ))}
      </div>

      <form className="portal-orderForm" onSubmit={handleSubmit}>
        <label>{selectedType.inputLabel}*</label>

        <input type="text" value={queryValue} onChange={(e) => setQueryValue(e.target.value)} required />

        <a
          className="portal-helpLink"
          href={`mailto:customerservice@gofoton.com${
            client.contactEmail ? `?cc=${encodeURIComponent(client.contactEmail)}` : ""
          }`}
        >
          NEED HELP?
        </a>

        <button className="portal-trackButton" type="submit" disabled={!queryValue.trim() || loadingOrders}>
          {loadingOrders ? "LOADING..." : "TRACK"}
        </button>
      </form>

      <div className="portal-orderDownloads">
        <div>
          <strong>Download Order Data</strong>

          <p>Download the latest Verizon order status files.</p>
        </div>

        <div className="portal-orderDownloadButtons">
          {client.openOrdersCSVURL && (
            <a className="portal-orangeButton" href={client.openOrdersCSVURL} target="_blank" rel="noopener noreferrer">
              Download Open Orders CSV
            </a>
          )}

          {client.shippedOrdersCSVURL && (
            <a
              className="portal-orangeButton"
              href={client.shippedOrdersCSVURL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Download Shipped Orders CSV
            </a>
          )}
        </div>
      </div>

      {searched && results.length === 0 && (
        <div className="portal-orderNoResults">
          No matching order was found. Check the number and try again, or contact your Go!Foton representative.
        </div>
      )}

      {results.length > 0 && (
        <div className="portal-orderResults">
          <div className="portal-orderResultsHeader">
            <h2>Order Information</h2>

            <span>
              {results.length} {results.length === 1 ? "line" : "lines"} found
            </span>
          </div>

          {results.map((order) => {
            const trackingNumber = getOrderTrackingNumber(order);

            const liveTracking = trackingNumber ? trackingByNumber[trackingNumber] : null;

            const trackingLoading = trackingNumber ? Boolean(loadingTrackingNumbers[trackingNumber]) : false;

            const trackingError = trackingNumber ? trackingErrors[trackingNumber] : "";

            const trackingURL = getCarrierTrackingURL(order, liveTracking);

            const progress = getShipmentProgress(order, liveTracking);

            return (
              <div className="portal-orderResultCard portal-orderResultCardEnhanced" key={order.id}>
                <div className="portal-orderCardHeader">
                  <div>
                    <div className="portal-orderCardEyebrow">PO Number</div>

                    <h3 className="portal-orderCardTitle">{order.poNumber || "—"}</h3>

                    <div className="portal-orderCardSubline">
                      Sales Order: {order.salesOrderNumber || "—"} | Line: {order.lineNumber || "—"}
                    </div>
                  </div>

                  <div className="portal-orderHeaderRight">
                    <CarrierBadge
                      carrier={trackingLoading ? "Checking Carrier" : liveTracking?.carrier || order.carrier}
                    />

                    <span
                      className={`portal-orderStatusBadge ${
                        progress.statusKey === "delivered"
                          ? "is-delivered"
                          : progress.statusKey === "in_transit"
                            ? "is-shipped"
                            : "is-open"
                      }`}
                    >
                      {progress.label}
                    </span>
                  </div>
                </div>

                <div className="portal-orderSummaryGrid">
                  <SummaryTile label="Tracking Number" value={trackingNumber || "Pending"} accent />

                  <SummaryTile
                    label="Estimated Arrival"
                    value={
                      liveTracking?.estimatedDelivery
                        ? formatDateValue(liveTracking.estimatedDelivery)
                        : cleanDateValue(order.expectedReceiptDate)
                    }
                  />

                  <SummaryTile
                    label="Ship Date"
                    value={
                      liveTracking?.shipDate ? formatDateValue(liveTracking.shipDate) : cleanDateValue(order.shipDate)
                    }
                  />

                  <SummaryTile
                    label="Current Location"
                    value={
                      trackingLoading
                        ? "Checking carrier..."
                        : liveTracking?.currentLocation ||
                          (trackingNumber ? "Awaiting carrier scan" : "Not shipped yet")
                    }
                  />
                </div>

                <ShipmentProgressBar progress={progress} />

                <div className="portal-orderDetailsGrid portal-orderDetailsGridEnhanced">
                  <OrderDetail label="MPN" value={order.mpn} />

                  <OrderDetail label="Customer Part Number" value={order.customerPartNumber} />

                  <OrderDetail label="Quantity" value={order.quantity} />

                  <OrderDetail label="Carrier" value={liveTracking?.carrier || order.carrier} />

                  <OrderDetail label="Service Level" value={liveTracking?.serviceLevel || order.serviceLevel} />

                  <OrderDetail
                    label="Destination / Jobsite"
                    value={liveTracking?.destination || order.shipToName || order.shipToAddress || order.shipToCity}
                  />

                  <OrderDetail label="Origin" value={liveTracking?.origin || order.origin} />

                  <OrderDetail label="Delivered At" value={formatDateWithTime(liveTracking?.deliveredAt, "")} />

                  <OrderDetail label="Signed By" value={liveTracking?.signedBy} />

                  <OrderDetail
                    label="Last Update"
                    value={liveTracking?.lastUpdate ? new Date(liveTracking.lastUpdate).toLocaleString() : "—"}
                  />
                </div>

                {trackingError && (
                  <div
                    className="portal-orderNoResults"
                    style={{
                      marginTop: 0,
                    }}
                  >
                    Live tracking error: {trackingError}
                  </div>
                )}

                {trackingLoading && <div className="portal-trackingLoading">Checking live carrier tracking...</div>}

                {liveTracking?.events?.length > 0 && (
                  <div className="portal-shipmentHistoryCard">
                    <div className="portal-historyHeader">
                      <h4>Package History</h4>

                      <span>{liveTracking.events.length} updates</span>
                    </div>

                    <div className="portal-historyScroller">
                      {liveTracking.events.map((event, index) => (
                        <div className="portal-historyItem" key={`${order.id}-${index}`}>
                          <div className="portal-historyDot" />

                          <div className="portal-historyContent">
                            <div className="portal-historyTopRow">
                              <strong>{event.description || event.status || "Shipment update"}</strong>

                              <span>{event.dateTime ? new Date(event.dateTime).toLocaleString() : ""}</span>
                            </div>

                            {event.location && <div className="portal-historyLocation">{event.location}</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="portal-orderActions">
                  {trackingURL && (
                    <a
                      href={trackingURL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="portal-orangeButton portal-orderTrackingButton"
                    >
                      Track on Carrier Website
                    </a>
                  )}

                  {liveTracking?.proofOfDeliveryUrl && (
                    <a
                      href={liveTracking.proofOfDeliveryUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="portal-orangeButton portal-orderTrackingButton"
                    >
                      View Proof of Delivery
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function OrderDetail({ label, value }) {
  if (value === undefined || value === null || String(value).trim() === "" || String(value).trim() === "—") {
    return null;
  }

  return (
    <div className="portal-orderDetail">
      <span>{label}</span>

      <strong>{value}</strong>
    </div>
  );
}

function SummaryTile({ label, value, accent = false, alwaysShow = false }) {
  const missing = value === undefined || value === null || String(value).trim() === "" || String(value).trim() === "—";

  if (missing && !alwaysShow) {
    return null;
  }

  return (
    <div className={`portal-summaryTile ${accent ? "is-accent" : ""}`}>
      <span>{label}</span>

      <strong>{value || "—"}</strong>
    </div>
  );
}

function CarrierBadge({ carrier }) {
  const label = normalizeCarrierName(carrier) || "Carrier Pending";

  const lower = label.toLowerCase();

  let logoURL = null;

  if (lower === "ups") {
    logoURL = "https://cdn.simpleicons.org/ups/351C15";
  } else if (lower === "fedex") {
    logoURL = "https://cdn.simpleicons.org/fedex/4D148C";
  } else if (lower === "usps") {
    logoURL = "https://cdn.simpleicons.org/usps/333366";
  } else if (lower === "dhl") {
    logoURL = "https://cdn.simpleicons.org/dhl/D40511";
  }

  return (
    <div className="portal-carrierBadge">
      {logoURL ? (
        <img src={logoURL} alt={`${label} logo`} className="portal-carrierLogo" />
      ) : (
        <div className="portal-carrierBadgeIcon">{label.charAt(0).toUpperCase()}</div>
      )}

      <span>{label}</span>
    </div>
  );
}

function ShipmentProgressBar({ progress }) {
  return (
    <div className="portal-progressCard">
      <div className="portal-progressTopRow">
        <h4>Order Status</h4>

        <span>{progress.label}</span>
      </div>

      <div className="portal-progressTrack">
        <div
          className="portal-progressTrackFill"
          style={{
            width: `${progress.fillPercent}%`,
          }}
        />

        {progress.steps.map((step, index) => {
          const isComplete = index < progress.activeStep;

          const isActive = index === progress.activeStep;

          return (
            <div
              key={step.key}
              className={`portal-progressStep ${isComplete ? "is-complete" : ""} ${isActive ? "is-active" : ""}`}
              style={{
                left: `${(index / (progress.steps.length - 1)) * 100}%`,
              }}
            >
              <div className="portal-progressDot">{isComplete || isActive ? "✓" : ""}</div>

              <div className="portal-progressLabel">{step.label}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function getShipmentProgress(order, trackingData) {
  const rawStatus = String(trackingData?.status || order?.status || "").toLowerCase();

  const delivered = rawStatus.includes("delivered") || Boolean(trackingData?.deliveredAt);

  const inTransit =
    !delivered &&
    (rawStatus.includes("transit") ||
      rawStatus.includes("out_for_delivery") ||
      rawStatus.includes("out for delivery") ||
      rawStatus.includes("exception") ||
      Boolean(trackingData?.currentLocation));

  const shipped =
    !delivered &&
    !inTransit &&
    (rawStatus.includes("shipped") ||
      rawStatus.includes("pre_transit") ||
      Boolean(getOrderTrackingNumber(order)) ||
      Boolean(trackingData?.shipDate));

  let activeStep = 0;

  let label = "Order Received";

  let statusKey = "open";

  if (delivered) {
    activeStep = 3;
    label = "Delivered";
    statusKey = "delivered";
  } else if (inTransit) {
    activeStep = 2;

    label = rawStatus.includes("out") ? "Out for Delivery" : "In Transit";

    statusKey = "in_transit";
  } else if (shipped) {
    activeStep = 1;
    label = "Shipped";
    statusKey = "shipped";
  }

  const steps = [
    {
      key: "ordered",
      label: "Ordered",
    },
    {
      key: "shipped",
      label: "Shipped",
    },
    {
      key: "transit",
      label: "In Transit",
    },
    {
      key: "delivered",
      label: "Delivered",
    },
  ];

  return {
    steps,

    activeStep,

    fillPercent: [0, 33, 66, 100][activeStep],

    label,

    statusKey,
  };
}

function formatDateValue(value) {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleDateString("en-US", {
    month: "numeric",
    day: "numeric",
    year: "numeric",
  });
}

function formatDateWithTime(dateValue, timeValue) {
  if (!dateValue && !timeValue) {
    return "—";
  }

  const formattedDate = formatDateValue(dateValue);

  return timeValue ? `${formattedDate} ${timeValue}` : formattedDate;
}

/* =====================================================
   DOCUMENTS
   ===================================================== */

function DocumentsView({ client }) {
  const documents = Array.isArray(client.documents) ? client.documents : [];

  return (
    <div className="portal-documents">
      <h2 className="portal-subsectionTitle">Shared Go!Foton Google Drive</h2>

      <div className="portal-documentGrid">
        <PortalResourceCard
          title="Master Parts List"
          image={ASSETS.masterParts}
          link={client.masterPartsListURL || client.sharedDriveUrl}
        />
      </div>

      <h2 className="portal-subsectionTitle">Knowledge Center</h2>

      <a href="/knowledge-center" className="portal-orangeButton portal-wideButton">
        View all documents
      </a>
    </div>
  );
}

function PortalResourceCard({ title, image, link }) {
  return (
    <div className="portal-resourceCard">
      <img src={image} alt={title} />

      <h3>{title}</h3>

      {link ? (
        <a className="portal-orangeButton" href={link} target="_blank" rel="noopener noreferrer">
          View
        </a>
      ) : (
        <button type="button" className="portal-orangeButton" disabled>
          View
        </button>
      )}
    </div>
  );
}

/* =====================================================
   CONTACT
   ===================================================== */

function ContactView({ client }) {
  return (
    <div className="portal-section">
      <div className="portal-contactViewGrid">
        <div className="portal-contactCard portal-contactCardLarge">
          <img
            src="https://firebasestorage.googleapis.com/v0/b/gofoton-55796.firebasestorage.app/o/stockImages%2F1779934321586.png?alt=media&token=af313ef8-93a6-4847-9b91-0b9bcc644fb3"
            alt={client.contactName || "Brian"}
            className="portal-contactPhoto portal-contactPhotoLarge"
          />

          <h2>Account Manager</h2>

          <p className="portal-contactName">{client.contactName || "Brian"}</p>

          {client.contactEmail && (
            <a href={`mailto:${client.contactEmail}`} className="portal-contactEmail">
              {client.contactEmail}
            </a>
          )}

          <div className="portal-contactButtons">
            <a className="portal-orangeButton" href={`mailto:${client.contactEmail || "sales@gofoton.com"}`}>
              Contact {client.contactName || "Brian"}
            </a>
          </div>
        </div>

        <div className="portal-contactCard portal-contactCardLarge">
          <img
            src="https://firebasestorage.googleapis.com/v0/b/gofoton-55796.firebasestorage.app/o/stockImages%2Fmahendra.jpeg?alt=media&token=7824d585-9d63-44c2-ab51-28931aecd1a1"
            alt="Mahendra Persad"
            className="portal-contactPhoto portal-contactPhotoLarge"
          />

          <h2>Technical Support</h2>

          <p className="portal-contactName">Mahendra Persad</p>

          <a href="mailto:mahendra.persad@gofoton.com" className="portal-contactEmail">
            mahendra.persad@gofoton.com
          </a>

          <div className="portal-contactButtons">
            <a className="portal-orangeButton" href="mailto:mahendra.persad@gofoton.com">
              Contact Mahendra
            </a>

            <a className="portal-orangeButton" href={`mailto:${client.supportEmail || "support@gofoton.com"}`}>
              Technical Support
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

/* =====================================================
   SETTINGS
   ===================================================== */

function SettingsView({ client, onLogout }) {
  return (
    <div className="portal-section">
      <div className="portal-largeCard">
        <h2>Account</h2>

        <div className="portal-settingRow">
          <span>Company</span>
          <strong>{client.name || "Client"}</strong>
        </div>

        <button type="button" className="portal-orangeButton" onClick={onLogout}>
          Sign Out
        </button>
      </div>
    </div>
  );
}
